import constants from '@/config/constants';

const nav = constants.nav;

//Vue Watcher
import { watch } from 'vue';

//Vue Router
import { createRouter, createWebHistory } from 'vue-router';

//Auth0 Route Guard
import { authGuard, useAuth0 } from '@auth0/auth0-vue';

//UI State Store
import { useUiStateStore } from '@/stores/uiState';

const guard = (to, from, next) => {
  //First, ensure the user is authenticated using Auth0's authGuard
  authGuard(to);

  //Get the roles required for the route from the route meta
  const requiredRoles = to.meta.rbac;

  //Use Auth0 to get ID token claims (which contains user info and roles)
  const { idTokenClaims } = useAuth0();

  //Function to check if the user has the required roles
  const checkAccess = (claims) => {

    const roles = claims[constants.auth.namespaces.roles] || []; //Get the users roles
    const hasAccess = requiredRoles && requiredRoles.some(role => roles.includes(role)); //Check if the required roles includes at least 1 user role

    if (hasAccess) {

      next(); //User has required role, proceed to the route

    } else {

      next({ name: nav.dashboard.name }); //Redirect to dashboard if user lacks required role
    }
  };

  if (!idTokenClaims.value) {

    //If ID token claims are not loaded yet, set up a watcher
    const unwatch = watch(() => idTokenClaims.value, (claims) => {

      if (claims) {

        unwatch(); //Stop watching after user data is loaded

        checkAccess(claims); //Check user roles
      }

    }, { immediate: true });

  } else {

    //If ID token claims are already available, perform role check directly
    checkAccess(idTokenClaims.value);
  }
};

//Vue Views
import DashboardView from '@/views/DashboardView';

const routes = [
  {
    path: '/',
    name: 'dashboard',
    meta: nav.dashboard.meta,
    component: DashboardView,
    beforeEnter: guard,
  },
  {
    path: '/controller',
    name: 'controller',
    meta: nav.controller.meta,
    component: () => import(/* webpackChunkName: 'controller' */ '../views/ControllerView'),
    beforeEnter: guard,

    children: [
      {
        path: ':siteUid',
        name: 'controller-siteuid',
        meta: nav.controller.children.controllerSiteUid.meta,
        component: () => import(/* webpackChunkName: 'controller' */ '../views/SubViews/ControllerSiteUidView'),
        beforeEnter: guard,
      },
    ],
  },
  {
    path: '/media',
    beforeEnter: guard,
    children: [
      {
        path: 'files',
        name: 'media-file-manager',
        meta: nav.media.children.fileManager.meta,
        component: () => import(/* webpackChunkName: 'media-file-manager' */ '../views/Media/FileManagerView'),
        beforeEnter: guard,
        children: [
          {
            path: ':siteUid',
            name: 'media-file-manager-site',
            meta: nav.media.children.fileManager.children.site.meta,
            component: () => import(/* webpackChunkName: 'media-file-manager' */ '../views/Media/SubViews/FileManagerSiteUidView'),
            beforeEnter: guard,
          },
        ],
      },
      {
        path: 'playlists',
        name: 'media-playlists',
        meta: nav.media.children.playlists.meta,
        component: () => import(/* webpackChunkName: 'media-playlists' */ '../views/Media/PlaylistsView'),
        beforeEnter: guard,
        children: [
          {
            path: ':siteUid',
            name: 'media-playlists-site',
            meta: nav.media.children.playlists.children.site.meta,
            component: () => import(/* webpackChunkName: 'media-playlists' */ '../views/Media/SubViews/PlaylistsSiteUidView'),
            beforeEnter: guard,
          },
        ],
      },
      {
        path: 'schedules',
        name: 'media-schedules',
        meta: nav.media.children.schedules.meta,
        component: () => import(/* webpackChunkName: 'media-schedules' */ '../views/Media/SchedulesView'),
        beforeEnter: guard,
        children: [
          {
            path: ':siteUid',
            name: 'media-schedules-site',
            meta: nav.media.children.schedules.children.site.meta,
            component: () => import(/* webpackChunkName: 'media-schedules' */ '../views/Media/SubViews/FileManagerSiteUidView'),
            beforeEnter: guard,
          },
        ],
      },
    ],
  },
  {
    path: '/settings',
    beforeEnter: guard,
    children: [
      {
        path: 'screens',
        name: 'settings-screens',
        meta: nav.settings.children.screens.meta,
        component: () => import(/* webpackChunkName: 'settings-screens' */ '../views/Settings/ScreensView'),
        beforeEnter: guard,
      },
      {
        path: 'screens/:code(\\d{8})',
        name: 'settings-screens-code',
        meta: nav.settings.children.screens.meta,
        component: () => import(/* webpackChunkName: 'settings-screens' */ '../views/Settings/ScreensView'),
        beforeEnter: guard,
      },
      {
        path: 'screens/:uid',
        name: 'settings-screen-edit',
        meta: nav.settings.children.screens.children.edit.meta,
        component: () => import(/* webpackChunkName: 'settings-screens' */ '../views/Settings/SubViews/EditScreenView'),
        beforeEnter: guard,
      },
      {
        path: 'org',
        name: 'settings-org',
        meta: nav.settings.children.org.meta,
        component: () => import(/* webpackChunkName: 'settings-org' */ '../views/Settings/OrgView'),
        beforeEnter: guard,
      }, {
        path: 'activity-log',
        name: 'settings-activity',
        meta: nav.settings.children.activity.meta,
        component: () => import(/* webpackChunkName: 'settings-org' */ '../views/Settings/ActivityLogView.vue'),
        beforeEnter: guard,
      },
      {
        path: 'sites',
        name: 'settings-sites',
        meta: nav.settings.children.sites.meta,
        component: () => import(/* webpackChunkName: 'settings-sites' */ '../views/Settings/SitesView'),
        beforeEnter: guard,
      },
      {
        path: 'sites/:uid',
        name: 'settings-site-edit',
        meta: nav.settings.children.sites.children.edit.meta,
        component: () => import(/* webpackChunkName: 'settings-sites' */ '../views/Settings/SubViews/EditSiteView'),
        beforeEnter: guard,
      },
      {
        path: 'users',
        name: 'settings-users',
        meta: nav.settings.children.users.meta,
        component: () => import(/* webpackChunkName: 'settings-users' */ '../views/Settings/UsersView'),
        beforeEnter: guard,
      },
      {
        path: 'users/:uid',
        name: 'settings-user-edit',
        meta: nav.settings.children.users.children.edit.meta,
        component: () => import(/* webpackChunkName: 'settings-users' */ '../views/Settings/SubViews/EditUserView'),
        beforeEnter: guard,
      },
      {
        path: 'billing',
        name: 'settings-billing',
        meta: nav.settings.children.billing.meta,
        component: () => import(/* webpackChunkName: 'settings-billing' */ '../views/Settings/BillingView'),
        beforeEnter: guard,
      },
      {
        path: 'billing/manage',
        name: 'settings-billing-manage',
        meta: nav.settings.children.billing.children.manage.meta,
        component: () => import(/* webpackChunkName: 'settings-billing' */ '../views/Settings/SubViews/EditUserView'),
        beforeEnter: guard,
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
});

router.beforeEach(() => {

  const uiState = useUiStateStore();
  uiState.setRouteLoading(true);
});

router.afterEach((to) => {

  //Set the title for each page
  document.title = `${to.meta.title ?? constants.app.meta.subName}\u00A0|\u00A0${constants.app.meta.name}`;

  const uiState = useUiStateStore();
  uiState.setRouteLoading(false);
});

router.onError((err) => {

  const uiState = useUiStateStore();

  //Set error message
  uiState.setRouteError(err.message || 'An unknown error occurred.');
});

export default router;
